<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="modalHeight"
      :maxWidth="modalWidth"
      @on-base-modal-close="cancelModal"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">
          {{ $t("dropdown") }}
        </div>
      </template>

      <element-description
        v-if="isDescriptionReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :value-container-class="'mt8 obj-select'"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ $t("dropdownData") }}
          </div>
        </div>
        <div class="expression">
          <search-box-simple
            id="dropdown-dp-search"
            class="search-box"
            :list="dataElementList"
            static-data-type="string"
            :value="dropdownStateExpression"
            :text-value="selectedTextValue"
            :has-new-token="true"
            :is-global="true"
            @on-search-box-item-selected="onDropdownElementSelected"
            @on-search-box-blur="onDataElementInputBlur"
          />
        </div>
      </div>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}
          </div>
        </div>
        <div class="expression">
          <expressions
            id="labelExpression"
            :data-elements="allSources"
            :data-type="'string'"
            :initial-expression="labelExpression"
            :is-global="true"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items" v-if="isCreating">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.OPTIONS) }}
          </div>
        </div>
        <div class="expression">
          <expressions
            id="optionsExpression"
            :data-elements="allSources"
            :isListExpression="true"
            :data-type="'string'"
            :initial-expression="optionsExpression"
            :is-global="true"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items" v-if="isCreating">
        <div class="label-wrapper">
          <div class="label">
            {{ $t("placeholder") }}
          </div>
        </div>
        <div class="expression">
          <expressions
            id="placeholderExpression"
            :data-elements="allSources"
            :data-type="'string'"
            :initial-expression="placeholderExpression"
            :is-global="true"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items" v-if="isCreating">
        <div class="label-wrapper">
          <div class="label">
            {{ $t("requireSelection") }}
          </div>
        </div>

        <div class="expression">
          <b-form-checkbox v-model="requireSelection"></b-form-checkbox>
        </div>
      </div>

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" @click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>
    </base-modal>
  </section>
</template>

<script lang="js">
  import { v4 as uuidv4 } from "uuid";
  import store from "@/store";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import Expressions from "@/components/Expressions"
  import ElementDescription from "@/components/ElementDescription";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import {DATA_ELEMENT_TYPE, MUTATION_NAME} from "@/static/global-variables";

  export default  {
    name: "add-dropdown-modal",
    components: {
      BaseModal,
      Expressions,
      ElementDescription,
      SearchBoxSimple
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      }
    },
    mounted () {
      if(this.editData) {
        this.labelExpression = this.editData.props.label;
        this.dropdownStateExpression = this.appContent.dataElements[this.editData.props.state];
        this.onDeDescriptionChanged(this.editData.props.description)
      }
      this.isDescriptionReady = true;
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isSubmitDisabled() {
        if (!!this.appContent && !this.appContent.isDeployable) {
          return true;
        }

        if (this.isCreating) {
          return !this.labelExpression || !this.optionsExpression;
        } else {
          return !this.dropdownStateExpression || !this.labelExpression;
        }
      },
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      allSources() {
        let output = null;
        if (this.appContent) {
          output = this.appContent.dataElements || {};
        } else {
          console.error(`No app content found for appId ${this.appId}`);
        }
        return output;
      },
      dataElementList() {
        return expressionBuilder.findDataElements(this.allSources, DATA_ELEMENT_TYPE.DROPDOWN);
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    data () {
      return {
        modalHeight: 500,
        modalWidth: 700,
        expression1: null,
        labelExpression: null,
        dropdownStateExpression: null,
        elementDescription: null,
        selectedTextValue: null,
        isCreating: null,
        optionsExpression: null,
        placeholderExpression: null,
        requireSelection: false,
        isDescriptionReady: false
      }
    },
    methods: {
      cancelModal() {
        const EVENT = "on-add-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, {id: this.id});
        }
      },
      createBtn() {
        let dataElementId;
        if (this.isCreating) {
          dataElementId = uuidv4();
          const dataElement = {
            appId: this.appId,
            id: dataElementId,
            displayElementRefCount: 0,
            name: this.dropdownStateExpression.value,
            description: "",
            fields: { Selection: { dataType: "String" } },
            type: DATA_ELEMENT_TYPE.DROPDOWN,
            options: this.optionsExpression,
            placeholder: this.placeholderExpression,
            requireSelection: this.requireSelection
        }
          store.commit(MUTATION_NAME.EDIT_DATA_PALETTES, dataElement);
        } else {
          dataElementId = this.dropdownStateExpression.id;
        }

        const obj = {
          modal: {
            id: this.id,
            create: "Dropdown",
            editElementId: this.editData ? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            data: {
              type: "Dropdown",
              label: this.labelExpression,
              state: dataElementId
            }
          }
        };

        const info = {
          appId: this.appId,
          dataElementId,
          prevState: this.editData ? this.editData.props.state : ""
        }
        store.commit(MUTATION_NAME.EDIT_DROPDOWN_REFERENCE, info);

        const EVENT = "on-add-modal-done";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, obj);
        }
      },

      onConfigExpressionValid(ctx) {
        const expValue = ctx.status ? ctx : null;
        const confExp = expValue ? expValue.confExpression : null;

        if(ctx.id === "optionsExpression") {
          this.optionsExpression = confExp;
        } else if (ctx.id === "labelExpression"){
          this.labelExpression = confExp;
        } else {
          this.placeholderExpression = confExp;
        }
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      },
      onDropdownElementSelected(ctx) {
        if ( !ctx || ctx.isEmpty) {
          this.dropdownStateExpression = null;
          this.isCreating = false;
        } else {
          const isStaticType = ctx.type === expressionBuilder.EXPRESSION_TYPE.STATIC;
          const isStringDataType = ctx.dataType === expressionBuilder.EXPRESSION_TYPE.STRING;
          this.isCreating = isStaticType && isStringDataType;
          this.dropdownStateExpression = this.isCreating ? ctx : this.appContent.dataElements[ctx.id];
        }
      },
      onDataElementInputBlur() {
        if(!this.dropdownStateExpression) {
          // clear the search box
          this.selectedTextValue = "";
          this.$nextTick(() => {
            // must trigger update to clear the box, set to null
            this.selectedTextValue = null;
          });
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .m-items {
    grid-template-columns: 8rem auto;
    margin-bottom: 10px;
    color: var(--DEFAULT_TEXT_BLACK);
    .label {
      overflow-wrap: anywhere;
      padding-top: 0.3rem;
    }
    .label-wrapper {
      padding-right: 1rem;
    }
    .expression,
    .label {
      display: flex;
      align-items: center;
      min-height: 2.5rem;
    }
    .progress-overflow {
      padding-top: 0;
    }
  }
  .m-title {
    text-align: left;
  }
</style>
